import { AuthState, UserRole } from '@tritt/firebase-api'
import type { UserInfo } from 'firebase/auth'

/**
 * Interface for auth adapters.
 * Allows swapping between real (Conta) and mock implementations.
 */
export interface AuthAdapter {
    /**
     * Initialize the auth adapter.
     * For real auth: connects to Firebase, checks SSO session.
     * For mock: no-op.
     */
    init(): Promise<void>

    /**
     * Perform login with email/password.
     */
    login(email: string, password: string): Promise<void>

    /**
     * Perform logout.
     */
    logout(): Promise<void>

    /**
     * Get current auth state.
     */
    getAuthState(): AuthState | null

    /**
     * Get access token for API calls.
     */
    getAccessToken(): Promise<string | undefined>

    /**
     * Register a callback for auth state changes.
     * Returns unsubscribe function.
     */
    onAuthStateChange(callback: (user: AuthState | null) => void): () => void
}

/**
 * Mock tenant type for Cypress tests.
 */
export interface MockTenant {
    organizationNumber: string
    organizationName: string
    organizationId?: string
    protected?: boolean
    roles?: UserRole[]
}

/**
 * Creates a mock AuthState with sensible defaults.
 */
export function createMockAuthState(overrides: Partial<AuthState> = {}): AuthState {
    return {
        token: 'mock-token',
        uid: 'mock-user-id',
        authenticated: true,
        email: 'test@conta.no',
        emailVerified: true,
        mfaEnabled: false,
        sessionId: 'mock-session-id',
        tid: undefined,
        isAnonymous: false,
        signInProvider: 'password',
        providerData: [] as UserInfo[],
        roles: [],
        ...overrides
    }
}
